/*
 * CIRP Dispatch - theme.js
 *
 * No dependencies. Everything is either a small custom element or a delegated
 * document listener, so sections keep working when the theme editor re-renders
 * them.
 *
 *   1. Setup
 *   2. Server detail validation (pure functions, exposed as window.cirp.validate)
 *   3. <provisioning-fields>
 *   4. <selling-plans>
 *   5. <variant-picker> and <product-info>
 *   6. <product-form>
 *   7. <product-recommendations> and <audio-player>
 *   8. Dropdowns, cart quantity, auto-submit, filters, deferred video
 */
(function () {
  'use strict';

  /* 1. Setup
     ------------------------------------------------------------------------ */

  const cirp = (window.cirp = window.cirp || {});

  cirp.routes = Object.assign(
    { cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', checkout: '/checkout' },
    cirp.routes || {}
  );

  cirp.strings = Object.assign(
    {
      addError: 'That could not be added to your cart. Please try again.',
      networkError: 'We could not reach the store. Check your connection and try again.',
      addToCart: 'Add to cart',
      soldOut: 'Sold out',
      unavailable: 'Unavailable',
    },
    cirp.strings || {}
  );

  // Indirection so the redirect can be observed in tests.
  cirp.navigate =
    cirp.navigate ||
    function (url) {
      window.location.assign(url);
    };

  function plainText(html) {
    const doc = new DOMParser().parseFromString(String(html == null ? '' : html), 'text/html');
    return (doc.body.textContent || '').trim();
  }

  function submitForm(form) {
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.submit();
    }
  }

  /* 2. Server detail validation
     These mirror the HTML pattern attributes in snippets/provisioning-fields.
     They are a convenience for customers, not a security boundary: whatever
     reads the order webhook must validate and escape these values again.
     ------------------------------------------------------------------------ */

  const NAME_PATTERN = /^[\p{L}\p{N}][\p{L}\p{N} ._&-]*$/u;
  const IPV4_PATTERN = /^(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;
  const HOST_LABEL_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
  const TLD_PATTERN = /^(?=.*[a-z])[a-z0-9-]{2,63}$/;
  const CFX_PATTERN = /(?:^|[/.\s])cfx\.re(?:[/:\s]|$)/i;

  function cleanName(raw) {
    return String(raw == null ? '' : raw)
      .replace(/\s+/g, ' ')
      .trim();
  }

  function validateName(raw) {
    const value = cleanName(raw);
    if (!value) return { value: value, error: 'name-required' };
    if (value.length < 2 || value.length > 64 || !NAME_PATTERN.test(value)) {
      return { value: value, error: 'name-invalid' };
    }
    return { value: value, error: null };
  }

  // Accepts what people actually paste: "connect 1.2.3.4:30120",
  // "https://play.example.com/", "fivem://connect/host:port", "HOST:30120".
  function parseAddress(raw) {
    let host = String(raw == null ? '' : raw)
      .trim()
      .toLowerCase();
    let port = null;

    host = host.replace(/^connect\s+/, '');
    host = host.replace(/^fivem:\/\/connect\//, '');
    host = host.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
    host = host.replace(/[/?#].*$/, '');

    const match = host.match(/^([^:]*):(\d{1,5})$/);
    if (match) {
      host = match[1];
      port = match[2];
    }

    host = host.replace(/\.+$/, '');
    return { host: host, port: port };
  }

  // Addresses that can never be a reachable server: unspecified, loopback,
  // broadcast, and the "0.0.0.0" example text copied in verbatim.
  const UNUSABLE_IPV4 = /^(?:0\.|127\.|255\.255\.255\.255$)/;

  function validateAddress(raw) {
    const original = String(raw == null ? '' : raw).trim();
    if (!original) return { value: '', port: null, error: 'address-required' };
    if (CFX_PATTERN.test(original)) return { value: original, port: null, error: 'address-cfx' };

    const parsed = parseAddress(original);
    const host = parsed.host;
    let valid = false;

    if (IPV4_PATTERN.test(host)) {
      valid = !UNUSABLE_IPV4.test(host);
    } else if (host.length <= 253 && host.indexOf('.') !== -1) {
      const labels = host.split('.');
      // A letter in the last label rules out malformed IPs such as 300.1.1.1.
      valid = labels.every((label) => HOST_LABEL_PATTERN.test(label)) && TLD_PATTERN.test(labels[labels.length - 1]);
    }

    if (!valid) return { value: original, port: null, error: 'address-invalid' };
    return { value: host, port: parsed.port, error: null };
  }

  function validatePort(raw) {
    const value = String(raw == null ? '' : raw).trim();
    if (!value) return { value: value, error: 'port-required' };
    if (!/^\d{1,5}$/.test(value)) return { value: value, error: 'port-invalid' };
    const number = Number(value);
    if (number < 1 || number > 65535) return { value: value, error: 'port-invalid' };
    return { value: String(number), error: null };
  }

  // Practical email check: one @, a sane local part without quote characters,
  // and a real-looking domain. Normalised to lower case for the bot.
  const EMAIL_LOCAL_PATTERN = /^[a-z0-9!#$%&*+/=?^_{|}~-]+(?:\.[a-z0-9!#$%&*+/=?^_{|}~-]+)*$/;

  function validateEmail(raw) {
    const value = String(raw == null ? '' : raw)
      .trim()
      .toLowerCase();
    if (!value) return { value: value, error: 'email-required' };
    const at = value.lastIndexOf('@');
    if (at < 1 || value.length > 254) return { value: value, error: 'email-invalid' };
    const local = value.slice(0, at);
    const domain = value.slice(at + 1);
    const labels = domain.split('.');
    const domainOk =
      labels.length >= 2 &&
      labels.every((label) => HOST_LABEL_PATTERN.test(label)) &&
      TLD_PATTERN.test(labels[labels.length - 1]);
    if (local.length > 64 || !EMAIL_LOCAL_PATTERN.test(local) || !domainOk) {
      return { value: value, error: 'email-invalid' };
    }
    return { value: value, error: null };
  }

  cirp.validate = {
    name: validateName,
    address: validateAddress,
    port: validatePort,
    email: validateEmail,
    parseAddress: parseAddress,
  };

  /* 3. <provisioning-fields>
     ------------------------------------------------------------------------ */

  class ProvisioningFields extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.addEventListener('input', (event) => this.onInput(event));
      this.addEventListener('focusout', (event) => this.onBlur(event));
      this.addEventListener('paste', (event) => this.onPaste(event));
      this.guardSubmitPress();
      this.updatePreview();
    }

    // Pressing the submit button blurs the field being edited. If that blur
    // revealed an error, the button would move before the press finished and
    // the click would be lost. So blur validation stands down during a press
    // and the submit handler validates everything instead.
    guardSubmitPress() {
      const form = this.closest('form');
      if (!form) return;
      const release = () => {
        window.clearTimeout(this.pressTimer);
        this.pressingSubmit = false;
      };
      const onPress = (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target || !target.closest('button[type="submit"], input[type="submit"]')) return;
        this.pressingSubmit = true;
        window.clearTimeout(this.pressTimer);
        this.pressTimer = window.setTimeout(release, 1000);
      };
      form.addEventListener('pointerdown', onPress);
      form.addEventListener('mousedown', onPress);
      // The press is over once the pointer lifts, wherever that happens.
      ['pointerup', 'pointercancel', 'mouseup'].forEach((type) => window.addEventListener(type, release));
    }

    get inputs() {
      return Array.from(this.querySelectorAll('[data-provision]'));
    }

    field(kind) {
      return this.querySelector('[data-provision="' + kind + '"]');
    }

    errorElement(input) {
      const ids = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      for (let index = 0; index < ids.length; index += 1) {
        const element = document.getElementById(ids[index]);
        if (element && element.classList.contains('field__error')) return element;
      }
      return null;
    }

    check(input) {
      switch (input.dataset.provision) {
        case 'name':
          return validateName(input.value);
        case 'address':
          return validateAddress(input.value);
        case 'port':
          return validatePort(input.value);
        case 'email':
          return validateEmail(input.value);
        default: {
          const value = input.value.trim();
          return { value: value, error: input.required && !value ? 'extra-required' : null };
        }
      }
    }

    setError(input, code) {
      const element = this.errorElement(input);
      if (code) {
        input.setAttribute('aria-invalid', 'true');
        if (element) {
          element.textContent = this.getAttribute('data-msg-' + code) || '';
          element.hidden = false;
        }
      } else {
        input.removeAttribute('aria-invalid');
        if (element) {
          element.textContent = '';
          element.hidden = true;
        }
      }
    }

    // commit: also write the cleaned value back (and move a pasted port across).
    validateField(input, commit) {
      const result = this.check(input);

      if (commit && !result.error) {
        if (input.value !== result.value) input.value = result.value;
        if (input.dataset.provision === 'address' && result.port) {
          const portInput = this.field('port');
          if (portInput) {
            portInput.value = result.port;
            this.validateField(portInput, true);
          }
        }
      }

      this.setError(input, result.error);
      return !result.error;
    }

    validate() {
      let firstInvalid = null;
      this.inputs.forEach((input) => {
        if (!this.validateField(input, true) && !firstInvalid) firstInvalid = input;
      });
      this.updatePreview();
      if (firstInvalid) {
        firstInvalid.focus();
        return false;
      }
      return true;
    }

    onInput(event) {
      const input = event.target.closest('[data-provision]');
      if (!input) return;
      if (input.getAttribute('aria-invalid') === 'true') this.validateField(input, false);
      this.updatePreview();
    }

    onBlur(event) {
      const input = event.target.closest('[data-provision]');
      if (!input || this.pressingSubmit) return;
      if (input.value.trim() !== '' || input.getAttribute('aria-invalid') === 'true') {
        this.validateField(input, true);
      }
      this.updatePreview();
    }

    onPaste(event) {
      const input = event.target.closest('[data-provision="address"]');
      if (!input) return;
      window.setTimeout(() => {
        if (!validateAddress(input.value).error) this.validateField(input, true);
        this.updatePreview();
      }, 0);
    }

    // The readout confirms what will be sent with the order, after clean-up.
    // It only lights up for values that pass validation.
    updatePreview() {
      const nameInput = this.field('name');
      const addressInput = this.field('address');
      const portInput = this.field('port');

      const name = validateName(nameInput ? nameInput.value : '');
      const address = validateAddress(addressInput ? addressInput.value : '');
      const port = validatePort((portInput && portInput.value.trim()) || address.port || '');

      this.setLine(this.querySelector('[data-preview-name]'), name.error ? '' : name.value, false);
      this.setLine(
        this.querySelector('[data-preview-connect]'),
        address.error ? '' : 'connect ' + address.value + ':' + (port.error ? '...' : port.value),
        Boolean(port.error)
      );
    }

    setLine(element, text, dim) {
      if (!element) return;
      element.textContent = text || element.getAttribute('data-empty') || '';
      element.classList.toggle('lcd__line--empty', !text || dim);
    }
  }

  /* 4. <selling-plans>
     Radios submit natively. This only keeps the visible price, its suffix,
     the "billed as" note and the billing disclosure in step with the plan.
     ------------------------------------------------------------------------ */

  class SellingPlans extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.addEventListener('change', () => this.sync());
    }

    get selected() {
      return this.querySelector('input[type="radio"][name="selling_plan"]:checked');
    }

    select(value) {
      const radios = Array.from(this.querySelectorAll('input[type="radio"][name="selling_plan"]'));
      const radio = radios.find((candidate) => candidate.value === String(value));
      if (!radio) return false;
      radio.checked = true;
      this.sync();
      return true;
    }

    sync() {
      const radio = this.selected;
      if (!radio) return;

      const root = this.closest('product-info') || document;
      const onPlan = radio.value !== '';
      const price = root.querySelector('[data-swap="price"] [data-price]');

      if (price) {
        const amount = plainText(radio.dataset.price);
        const compare = plainText(radio.dataset.compare);

        price
          .querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale')
          .forEach((element) => {
            element.textContent = amount;
          });

        const was = price.querySelector('.price__sale s');
        if (was) was.textContent = compare;

        price.classList.toggle('price--on-sale', Boolean(compare));
        price.classList.toggle('price--on-plan', onPlan);

        const suffix = price.querySelector('[data-price-suffix]');
        if (suffix) {
          const suffixText = plainText(radio.dataset.suffix);
          suffix.textContent = suffixText;
          suffix.hidden = !suffixText;
        }

        const note = price.querySelector('[data-price-note]');
        if (note) {
          const noteText = plainText(radio.dataset.note);
          note.textContent = noteText;
          note.hidden = !noteText;
        }
      }

      const terms = root.querySelector('[data-subscription-terms]');
      if (terms) terms.hidden = !onPlan;
    }
  }

  /* 5. <variant-picker> and <product-info>
     ------------------------------------------------------------------------ */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      const data = this.querySelector('[data-variants]');
      try {
        this.variants = data ? JSON.parse(data.textContent) : [];
      } catch (error) {
        this.variants = [];
      }

      this.addEventListener('change', () => this.onChange());
    }

    onChange() {
      const selected = Array.from(this.querySelectorAll('fieldset')).map((group) => {
        const checked = group.querySelector('input:checked');
        return checked ? checked.value : null;
      });

      const variant = this.variants.find((candidate) =>
        candidate.options.every((option, index) => option === selected[index])
      );

      const info = this.closest('product-info');
      if (info && typeof info.setVariant === 'function') info.setVariant(variant || null);
    }
  }

  class ProductInfo extends HTMLElement {
    setVariant(variant) {
      if (!variant) {
        this.setSubmitState(false, cirp.strings.unavailable);
        return;
      }

      this.querySelectorAll('input[data-variant-id]').forEach((input) => {
        input.value = variant.id;
      });

      this.setSubmitState(variant.available, variant.available ? cirp.strings.addToCart : cirp.strings.soldOut);
      this.updateUrl(variant.id);
      this.showMedia(variant.media_id);
      return this.refresh(variant);
    }

    setSubmitState(enabled, label) {
      const button = this.querySelector('[data-swap="submit"] button[type="submit"]');
      if (!button) return;
      button.disabled = !enabled;
      const text = button.querySelector('[data-submit-label]');
      if (text && label) text.textContent = label;
    }

    updateUrl(variantId) {
      if (!window.history || typeof window.history.replaceState !== 'function') return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    }

    showMedia(mediaId) {
      const gallery = this.querySelector('[data-gallery]');
      if (!gallery || !mediaId) return;
      const item = gallery.querySelector('[data-media-id="' + mediaId + '"]');
      if (!item || item === gallery.firstElementChild) return;

      gallery.querySelectorAll('.gallery__item--lead').forEach((element) => {
        element.classList.remove('gallery__item--lead');
      });
      item.classList.add('gallery__item--lead');
      gallery.prepend(item);
      gallery.scrollLeft = 0;
    }

    // Re-render this section for the chosen variant and swap in the parts that
    // depend on it. What the customer has typed elsewhere in the form is kept.
    async refresh(variant) {
      if (!this.dataset.url || !this.dataset.section) return;
      if (this.controller) this.controller.abort();
      this.controller = typeof AbortController === 'function' ? new AbortController() : null;

      const plans = this.querySelector('selling-plans');
      const previousPlan = plans && plans.selected ? plans.selected.value : null;

      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('variant', variant.id);
      url.searchParams.set('section_id', this.dataset.section);
      // Liquid then renders the price and selector for the plan already chosen.
      if (previousPlan) url.searchParams.set('selling_plan', previousPlan);

      try {
        const response = await fetch(url.toString(), this.controller ? { signal: this.controller.signal } : {});
        if (!response.ok) throw new Error('Section request failed: ' + response.status);
        const html = await response.text();
        const next = new DOMParser().parseFromString(html, 'text/html');

        ['price', 'plans', 'submit'].forEach((name) => {
          const current = this.querySelector('[data-swap="' + name + '"]');
          const incoming = next.querySelector('[data-swap="' + name + '"]');
          if (current && incoming) current.innerHTML = incoming.innerHTML;
        });

        const freshPlans = this.querySelector('selling-plans');
        if (freshPlans && typeof freshPlans.select === 'function') {
          if (previousPlan === null || !freshPlans.select(previousPlan)) freshPlans.sync();
        }
      } catch (error) {
        // Aborted or offline: the optimistic button state from setVariant stands.
      }
    }
  }

  /* 6. <product-form>
     ------------------------------------------------------------------------ */

  function describeCartError(data) {
    if (!data) return '';
    const detail = data.description || data.message || '';
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object') {
      return Object.keys(detail)
        .map((key) => [].concat(detail[key]).join(' '))
        .join(' ');
    }
    return '';
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.ready = true;

      // Validation runs in onSubmit so customers get our messages instead of
      // the browser's. The HTML attributes remain as the no-JavaScript fallback.
      this.form.noValidate = true;
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    get submitButton() {
      return this.form.querySelector('button[type="submit"]');
    }

    setBusy(busy) {
      this.busy = busy;
      const button = this.submitButton;
      if (!button) return;
      if (busy) {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    }

    showError(message) {
      const element = this.form.querySelector('[data-form-error]');
      if (!element) return;
      element.textContent = message || '';
      element.hidden = !message;
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.busy) return;

      const button = this.submitButton;
      if (button && button.disabled) return;

      this.showError('');

      const provisioning = this.form.querySelector('provisioning-fields');
      if (provisioning && typeof provisioning.validate === 'function' && !provisioning.validate()) return;
      if (typeof this.form.reportValidity === 'function' && !this.form.reportValidity()) return;

      this.setBusy(true);

      let response;
      let data = null;

      try {
        response = await fetch(cirp.routes.cartAdd + '.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(this.form),
        });
      } catch (error) {
        this.setBusy(false);
        this.showError(cirp.strings.networkError);
        return;
      }

      try {
        data = await response.json();
      } catch (error) {
        data = null;
      }

      if (!response.ok || !data || data.status) {
        this.setBusy(false);
        this.showError(describeCartError(data) || cirp.strings.addError);
        return;
      }

      cirp.navigate(cirp.afterAdd === 'checkout' ? cirp.routes.checkout : cirp.routes.cart);
    }
  }

  /* 7. <product-recommendations>
     ------------------------------------------------------------------------ */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.requested || !this.dataset.url || this.children.length > 0) return;

      const load = () => {
        if (this.requested) return;
        this.requested = true;
        fetch(this.dataset.url)
          .then((response) => (response.ok ? response.text() : ''))
          .then((html) => {
            if (!html) return;
            const next = new DOMParser().parseFromString(html, 'text/html').querySelector('product-recommendations');
            if (next && next.innerHTML.trim()) this.innerHTML = next.innerHTML;
          })
          .catch(() => {});
      };

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries, self) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            self.disconnect();
            load();
          },
          { rootMargin: '0px 0px 600px 0px' }
        );
        observer.observe(this);
      } else {
        load();
      }
    }
  }

  /* <audio-player>
     Custom controls over a native <audio>. The native controls are the
     no-JavaScript fallback and are switched off here. Only one clip plays
     at a time.
     ------------------------------------------------------------------------ */

  function formatTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) return '--:--';
    const whole = Math.floor(seconds);
    const minutes = Math.floor(whole / 60);
    const rest = whole % 60;
    return minutes + ':' + (rest < 10 ? '0' : '') + rest;
  }

  class AudioPlayer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.audio = this.querySelector('audio');
      this.ui = this.querySelector('[data-audio-ui]');
      if (!this.audio || !this.ui) return;
      this.ready = true;

      this.toggle = this.querySelector('[data-audio-toggle]');
      this.seek = this.querySelector('[data-audio-seek]');
      this.current = this.querySelector('[data-audio-current]');
      this.duration = this.querySelector('[data-audio-duration]');
      this.led = this.querySelector('[data-audio-led]');
      this.error = this.querySelector('[data-audio-error]');
      this.scrubbing = false;

      this.audio.removeAttribute('controls');
      this.ui.hidden = false;
      this.classList.add('audio--ready');

      this.toggle.addEventListener('click', () => this.togglePlayback());
      this.audio.addEventListener('play', () => this.onPlay());
      this.audio.addEventListener('pause', () => this.setPlaying(false));
      this.audio.addEventListener('ended', () => this.onEnded());
      this.audio.addEventListener('timeupdate', () => this.updateTime());
      this.audio.addEventListener('loadedmetadata', () => this.updateDuration());
      this.audio.addEventListener('durationchange', () => this.updateDuration());
      this.audio.addEventListener('error', () => this.showError());

      if (this.seek) {
        this.seek.addEventListener('pointerdown', () => {
          this.scrubbing = true;
        });
        this.seek.addEventListener('input', () => this.onSeek(false));
        this.seek.addEventListener('change', () => this.onSeek(true));
      }

      this.updateDuration();
    }

    togglePlayback() {
      if (this.audio.paused) {
        const attempt = this.audio.play();
        if (attempt && typeof attempt.catch === 'function') attempt.catch(() => this.showError());
      } else {
        this.audio.pause();
      }
    }

    onPlay() {
      document.querySelectorAll('audio-player audio').forEach((other) => {
        if (other !== this.audio && !other.paused) other.pause();
      });
      if (this.error) this.error.hidden = true;
      this.setPlaying(true);
    }

    onEnded() {
      this.setPlaying(false);
      if (!this.audio.loop) {
        this.audio.currentTime = 0;
        this.updateTime();
      }
    }

    setPlaying(playing) {
      this.classList.toggle('audio--playing', playing);
      if (this.toggle) {
        this.toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
        const label = playing ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay;
        if (label) this.toggle.setAttribute('aria-label', label);
      }
      if (this.led) this.led.classList.toggle('led--on', playing);
    }

    updateDuration() {
      const total = this.audio.duration;
      if (this.duration) this.duration.textContent = formatTime(total);
      if (this.seek && isFinite(total) && total > 0) {
        this.seek.max = String(total);
        this.seek.disabled = false;
      }
    }

    updateTime() {
      const now = this.audio.currentTime || 0;
      if (this.current) this.current.textContent = formatTime(now);
      if (this.seek && !this.scrubbing) {
        this.seek.value = String(now);
        this.seek.setAttribute('aria-valuetext', formatTime(now));
      }
    }

    onSeek(commit) {
      if (!this.seek) return;
      const target = Number(this.seek.value);
      if (!isFinite(target)) return;
      if (this.current) this.current.textContent = formatTime(target);
      this.seek.setAttribute('aria-valuetext', formatTime(target));
      if (commit) {
        this.scrubbing = false;
        if (isFinite(this.audio.duration)) this.audio.currentTime = target;
      }
    }

    showError() {
      this.setPlaying(false);
      if (this.error) this.error.hidden = false;
      if (this.toggle) this.toggle.disabled = true;
      if (this.seek) this.seek.disabled = true;
    }
  }

  const elements = {
    'provisioning-fields': ProvisioningFields,
    'selling-plans': SellingPlans,
    'variant-picker': VariantPicker,
    'product-info': ProductInfo,
    'product-form': ProductForm,
    'product-recommendations': ProductRecommendations,
    'audio-player': AudioPlayer,
  };

  if (window.customElements) {
    Object.keys(elements).forEach((name) => {
      if (!window.customElements.get(name)) window.customElements.define(name, elements[name]);
    });
  }

  /* 8. Dropdowns, cart quantity, auto-submit, filters, deferred video
     ------------------------------------------------------------------------ */

  // Deferred video embeds (snippets/video-embed): the poster and play link
  // are replaced by the player only when the visitor presses play.
  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-video-play]') : null;
    if (!trigger) return;
    const host = trigger.closest('[data-video-embed]');
    if (!host || !host.dataset.embedSrc) return;
    event.preventDefault();

    const iframe = document.createElement('iframe');
    iframe.src = host.dataset.embedSrc;
    iframe.title = host.dataset.embedTitle || '';
    iframe.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
    );
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');

    host.innerHTML = '';
    host.appendChild(iframe);
    host.classList.add('video-embed--playing');
    iframe.focus();
  });

  const DROPDOWNS = 'details[data-header-dropdown], details[data-dropdown]';
  const DISMISSIBLE = DROPDOWNS + ', details[data-header-drawer]';

  document.addEventListener('click', (event) => {
    document.querySelectorAll(DISMISSIBLE).forEach((details) => {
      if (details.open && !details.contains(event.target)) details.removeAttribute('open');
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const origin = event.target instanceof Element ? event.target : null;
    const within = origin ? origin.closest(DISMISSIBLE) : null;
    const open =
      within && within.open
        ? within
        : Array.from(document.querySelectorAll(DISMISSIBLE)).find((details) => details.open);
    if (!open) return;
    open.removeAttribute('open');
    const summary = open.querySelector('summary');
    if (summary) summary.focus();
  });

  // Only one dropdown open at a time. "toggle" does not bubble, so capture it.
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof Element) || !details.matches(DROPDOWNS) || !details.open) return;
      document.querySelectorAll(DROPDOWNS).forEach((other) => {
        if (other !== details && other.open) other.removeAttribute('open');
      });
    },
    true
  );

  let cartTimer = null;

  function updateCart(form) {
    if (form.dataset.submitting === 'true') return;
    form.dataset.submitting = 'true';
    form.submit();
  }

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.matches('[data-cart-quantity]') && target.form) {
      window.clearTimeout(cartTimer);
      cartTimer = window.setTimeout(() => updateCart(target.form), 350);
      return;
    }

    if (target.matches('[data-auto-submit]') && target.form) submitForm(target.form);
  });

  // Enter in a quantity box should update the cart, not jump to checkout.
  document.addEventListener('keydown', (event) => {
    const target = event.target;
    if (event.key !== 'Enter' || !(target instanceof Element) || !target.matches('[data-cart-quantity]')) return;
    if (!target.form) return;
    event.preventDefault();
    window.clearTimeout(cartTimer);
    updateCart(target.form);
  });

  // Keep empty price boxes out of the filter URL.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof Element) || !form.matches('form[data-facets]')) return;
    Array.from(form.elements).forEach((element) => {
      if (element.type === 'number' && element.value === '') {
        element.disabled = true;
        element.setAttribute('data-emptied', '');
      }
    });
  });

  // Coming back through the back/forward cache: undo transient state.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('[data-emptied]').forEach((element) => {
      element.disabled = false;
      element.removeAttribute('data-emptied');
    });
    document.querySelectorAll('form[data-submitting]').forEach((form) => {
      form.removeAttribute('data-submitting');
    });
    document.querySelectorAll('product-form').forEach((element) => {
      if (typeof element.setBusy === 'function' && element.form) element.setBusy(false);
    });
  });
})();
